import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, EditableToken } from '@vibesites/runtime/react';

/**
 * Pull a pixel height out of a postMessage payload. External embeds use a few
 * different conventions, so probe the common ones:
 *   - a bare number / numeric string
 *   - iframe-resizer's "[iFrameSizer]msgId:height:width:..." string
 *   - an object with a height-ish key
 * Returns null when nothing usable is present.
 */
function readPostedHeight(data: unknown): number | null {
  if (typeof data === 'number') return Number.isFinite(data) ? data : null;
  if (typeof data === 'string') {
    const m = data.match(/\[iFrameSizer\][^:]*:(\d+(?:\.\d+)?):/);
    if (m) return parseFloat(m[1]);
    const n = Number(data);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    for (const k of ['height', 'frameHeight', 'iframeHeight', 'scrollHeight', 'documentHeight']) {
      const v = o[k];
      if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
      if (typeof v === 'string' && Number(v) > 0) return Number(v);
    }
  }
  return null;
}

export function TourSchedulerEmbed() {
  const url         = useContentToken('tourscheduler.url');
  const heightToken = useContentToken('tourscheduler.height');
  // The configured value is the DESKTOP fallback height. Smaller screens scale
  // it up via CSS (see `.tse-frame` media queries) because the form stacks
  // taller as the column narrows.
  const fallbackPx  = Math.max(300, parseInt(heightToken || '1100', 10) || 1100);

  // When the embed reports its content height via postMessage we size the
  // iframe to fit — no internal scrollbar, no clipping, correct at every
  // breakpoint. Until (or unless) that arrives, the configured height is the
  // fallback so the form is still usable for embeds that stay silent.
  const [autoHeight, setAutoHeight] = React.useState<number | null>(null);

  const allowedOrigin = React.useMemo(() => {
    if (!url) return null;
    try { return new URL(url).origin; } catch { return null; }
  }, [url]);

  React.useEffect(() => {
    if (!url) return;
    function onMessage(e: MessageEvent) {
      // Only trust height reports coming from the embed's own origin.
      if (allowedOrigin && e.origin !== allowedOrigin) return;
      const h = readPostedHeight(e.data);
      if (h && h >= 300) setAutoHeight(Math.ceil(h));
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [url, allowedOrigin]);


  return (
    <section className="tse-section" style={{ '--tse-h': `${fallbackPx}px` } as React.CSSProperties}>
      <EditableToken path="tourscheduler.url" as="div" className="tse-wrap">
        {url ? (
          <iframe
            src={url}
            title="Tour Scheduler"
            className="tse-frame"
            /**
             * scrolling="no" is the only cross-browser attribute that
             * suppresses an iframe's own internal scrollbars (both axes), so
             * the page scrolls, not the iframe.
             *
             * Height resolution, in priority order:
             *   1. If the embed reports its content height via postMessage,
             *      the inline `height` below wins and fits the form exactly.
             *   2. Otherwise the `.tse-frame` CSS controls height — the
             *      editor's value is the desktop fallback, scaled up on
             *      narrower screens where the form stacks taller.
             */
            scrolling="no"
            style={{
              display: 'block',
              width: '100%',
              border: 'none',
              transition: 'height 0.2s ease',
              // Only force a height once the embed reports one; otherwise let
              // the responsive CSS fallback apply.
              ...(autoHeight ? { height: `${autoHeight}px` } : null),
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <div className="tse-placeholder">
            <p className="tse-placeholder-title">Tour Scheduler not configured</p>
            <p className="tse-placeholder-hint">
              Set the <strong>Scheduler URL</strong> field on this section to embed your tour
              scheduler. The URL auto-populates from the property's
              <code>custom_eliseai_tour_scheduler_url</code> field when available.
            </p>
          </div>
        )}
      </EditableToken>

      {/* Height is editor-only; hidden anchor satisfies token-missing-editable-wrapper */}
      <EditableToken path="tourscheduler.height" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        .tse-section {
          background: var(--vibe-color-background);
        }
        .tse-wrap {
          width: 100%;
        }
        /* Responsive fallback height, used when the embed doesn't report its
           own height. --tse-h is the desktop value from the editor; narrower
           screens get more room because the form stacks taller. An inline
           height (set only when the embed posts a height) overrides all of
           these. */
        .tse-frame {
          height: calc(var(--tse-h, 1100px) * 1.5);
        }
        @media (min-width: 640px) {
          .tse-frame { height: calc(var(--tse-h, 1100px) * 1.25); }
        }
        @media (min-width: 1024px) {
          .tse-frame { height: var(--tse-h, 1100px); }
        }
        .tse-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 4rem 2rem;
          background: var(--vibe-color-surface, #f5f5f5);
          border: 2px dashed var(--vibe-color-border, #ccc);
          text-align: center;
        }
        .tse-placeholder-title {
          font-family: var(--vibe-font-heading);
          font-weight: 600;
          font-size: 1rem;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .tse-placeholder-hint {
          font-size: 0.875rem;
          color: var(--vibe-color-text-muted, #888);
          max-width: 36rem;
          margin: 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TourSchedulerEmbed',
  displayName: 'Tour Scheduler Embed',
  tokens: {
    'tourscheduler.url': {
      label: 'Scheduler URL',
      type: 'url',
      defaultField: 'property_custom_eliseai_tour_scheduler_url',
      defaultValue: '',
      description: 'URL for the tour scheduler. Auto-populates from the property\'s Elise AI custom field when set.',
    },
    'tourscheduler.height': {
      label: 'Desktop height (px)',
      type: 'text',
      defaultValue: '1100',
      description: 'Fallback height for the scheduler on desktop. Narrower screens scale this up automatically, since the form stacks taller as the column narrows. Set it tall enough that the full form shows on desktop without an inner scrollbar — the page scrolls, not the iframe.',
    },
  },
};

(TourSchedulerEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TourSchedulerEmbed;
